'use strict';

const BaseView = require('components-framework/view/base.view');
const withRenderedMain = require('components-framework/view/with_rendered_main');
const withCachedSelectors = require('components-framework/view/with_cached_selectors');
const cashRefundFactory = require('components/user_area/cash_refund/js/main');
const involuntaryCancellationFactory = require('components/user_area/involuntary_cancellation/js/main');
const openTicketFactory = require('components/user_area/open_ticket/js/main');
const fetcher = require('od-modules/utils/fetcher');
const trackingUtils = require('od-modules/utils/tracking.utils');
const openTicketRepository = require('od-modules/open_ticket_redemption_flow/openticket_repository');
const incidentsManagerUtils = require('./utils/utils');
const notificationHelper = require('components/user_area/notifications_manager/js/utils/notificationHelper');
const intermediateLayerHelper = require('./utils/intermediateLayerHelper');

const {
    BIM_STATUS,
    BIM_CANCELLATION_STATUS,
    BIM_CANCELLATION_STATUS_GROUPS,
    BIM_UPDATE_CONSENT,
    SURVEY_BRAND_PARAM_MAP,
    SURVEY_LANG_EXCEPTIONS,
    TRACKING,
    BIM_CANCELLATION_TYPE,
} = require('./utils/constants');

module.exports = withRenderedMain(withCachedSelectors(BaseView.extend({
    templateSettings: {
        evaluate: /\{\{#([\s\S]+?)\}\}/g,
        interpolate: /\{\{[^#{]([\s\S]+?)[^}]\}\}/g,
        escape: /\{\{\{([\s\S]+?)\}\}\}/g,
    },

    selectors: {
        openTicketWrapper: '.open_ticket_wrapper',
        cashRefundWrapper: '.cash_refund_wrapper',
        involuntaryCancellationWrapper: '.involuntary_cancellation_wrapper',
    },

    initialize({parentWidget, bookingData: bookingInfo, trackingInfo}) {
        this.reset();
        this.parentWidget = parentWidget;
        this.model.set({
            bookingID: bookingInfo.bookingBasicInfo.id,
            cancelledPNRs: bookingInfo.cancelledPNRs,
            messages: bookingInfo.messages,
            bookingInfo,
            trackingInfo,
            isIntermediateLayerManager: Odigeo.AB.isIntermediateLayerManager,
            isBIM: Odigeo.AB.isBookingImportantMessage,
            contactAirlineAirlineInitDateVY: this.keys.contactAirlineAirlineInitDateVY,
        });
        this.listenTo(this.model, 'renderCancellations', this.renderCancellations);
        this.listenTo(this.model, 'trackInvalidCancellationProviderItem', this.trackInvalidCancellationProviderItem);
        if (this.model.get('isBIM')) {
            if (bookingInfo.messages.length > 0) {
                this.model.processMessages(bookingInfo.messages).then(() => this.renderCancellations());
            } else {
                this.persistBIM(false);
            }
        } else {
            this.model.requestBookingIncidents();
        }
    },

    reset() {
        this.destroyWidget(this.cashRefund);
        this.destroyWidget(this.involuntaryCancellation);
        this.destroyWidget(this.openTicket);
        this.model.reset();
    },

    destroyWidget(widget) {
        widget = widget ? widget.destroy() : null;
    },

    renderCancellations() {
        this.model.set('renderPNRCards', this.isRenderPNRCards());
        if (!this.model.get('avoidReload')) {
            this.instantiateWidgets();
        }
        this.setNotificationsToDisplay();
        if (!this.model.get('isBIM')) {
            this.parentWidget.showSegmentsItineraryBox();
        }
    },

    instantiateWidgets() {
        this.setSectionNames(this.model.get('bookingInfo'));
        this.createOpenTicketIfNeeded();
        this.createCashRefundIfNeeded();
        this.createInvoluntaryCancellationIfNeeded();
    },

    createCashRefundIfNeeded() {
        const cashRefundPNRs = this.model.get('cashRefundPNRs');
        if (!Odigeo._.isEmpty(cashRefundPNRs)) {
            this.cashRefund = cashRefundFactory.create({
                el: this.$cachedSelectors.cashRefundWrapper,
                parentWidget: this,
                cashRefundPNRs,
                bookingInfo: this.model.get('bookingInfo'),
                trackingInfo: this.model.get('trackingInfo'),
                options: {renderPNRCards: this.model.get('renderPNRCards')},
            });
        }
    },

    createInvoluntaryCancellationIfNeeded() {
        const involuntaryCancellationPNRs = this.model.get('involuntaryCancellationPNRs');
        if (!Odigeo._.isEmpty(involuntaryCancellationPNRs)) {
            this.involuntaryCancellation = involuntaryCancellationFactory.create({
                el: this.$cachedSelectors.involuntaryCancellationWrapper,
                parentWidget: this,
                involuntaryCancellationPNRs,
                bookingInfo: this.model.get('bookingInfo'),
                trackingInfo: this.model.get('trackingInfo'),
                options: {renderPNRCards: this.model.get('renderPNRCards')},
            });
        }
    },

    createOpenTicketIfNeeded() {
        const openTicketPNRs = this.model.get('openTicketPNRs');
        if (!Odigeo._.isEmpty(openTicketPNRs)) {
            const bookingInfo = this.model.get('bookingInfo');
            const {buyer, bookingBasicInfo} = bookingInfo;
            this.openTicket = openTicketFactory.create({
                el: this.$cachedSelectors.openTicketWrapper,
                parentWidget: this,
                openTicketPNRs: Object.assign({}, openTicketPNRs),
                trackingInfo: this.model.get('trackingInfo'),
                bookingId: this.model.get('bookingID'),
                buyerName: buyer.name,
                buyerMail: buyer.mail,
                bookingCreationDate: bookingBasicInfo.creationDate,
                acceptedPNRs: [].concat(this.model.get('acceptedPNRs')),
                pendingPNRs: [].concat(this.model.get('pendingPNRs')),
                rejectedPNRs: [].concat(this.model.get('rejectedPNRs')),
                redemptionPendingPNRs: [].concat(this.model.get('redemptionPendingPNRs')),
                openTicketRepository: openTicketRepository(fetcher, Odigeo.Settings.getProperty('bookingChangeRequestUrlFormatter')),
                bookingInfo,
                options: {renderPNRCards: this.model.get('renderPNRCards')},
            });
        } else {
            this.parentWidget.showFlightStatusMessage();
        }
    },

    // (RE-)RENDERING
    setNotificationsToDisplay() {
        this.deleteAllNotifications();
        const notificationsToDisplay = this.model.get('isBIM') ? this.getBIMNotifications() : this.getNotificationsToDisplay();
        if (notificationsToDisplay.length > 0) {
            if (!this.model.get('renderPNRCards')) {
                this.renderCancellationsNotifications(notificationsToDisplay);
                this.trackWidgetOnload(notificationsToDisplay);
            } else {
                this.trackWidgetOnloadLegacy();
            }
        }
    },

    getBIMNotifications() {
        return this.model.get('messages').map(({status, extraHeader, cancellations, semantic, cancellationType}) => {
            const infoPNRs = this.model.getInfoPNRs(cancellations);
            const notification = {
                ...{status: this.getStatusNotification(status, infoPNRs)},
                extraHeader,
                semantic,
                cancellationType,
                pnrsIncluded: infoPNRs,
            };
            const trackingInfo = this.getCancellationCTATrackingInfo(notification);
            this.setNotificationCTAHandler(trackingInfo, notification);
            return notification;
        }, this);
    },

    getStatusNotification(messageStatus, infoPNRs) {
        const cancellations = Object.values(infoPNRs);
        if ([BIM_STATUS.REFUND_NOT_MANAGED_BY_EDO, BIM_STATUS.ALL_REFUND_NOT_MANAGED_BY_EDO].includes(messageStatus) &&
            cancellations.every(({cancellationStatus}) => BIM_CANCELLATION_STATUS.CONTACT_AIRLINE === cancellationStatus)) {
            messageStatus = cancellations.length > 1 ? BIM_STATUS.ALL_CONTACT_AIRLINE : BIM_STATUS.CONTACT_AIRLINE;
        }

        return messageStatus;
    },

    getNotificationsToDisplay() {
        const disableExtraHeader = Object.keys(this.getPNRsByCancellation(
            ...Object.keys(BIM_CANCELLATION_STATUS_GROUPS.CRITICAL.CANCELLATIONS)
        )).length !== 0;
        return Odigeo._.reduce(BIM_CANCELLATION_STATUS_GROUPS, (notifications, {CANCELLATIONS, MAPPING_FN}, semantic) => {
            const pnrsIncluded = this.getPNRsByCancellation(...Object.keys(CANCELLATIONS));
            const cancellations = Odigeo._.map(pnrsIncluded, 'cancellationStatus').map(MAPPING_FN || Odigeo._.identity);
            const status = this.getNotificationDisplayedForCancellationGroup(semantic, pnrsIncluded, cancellations);
            if (status !== undefined) {
                const notificationData = {
                    status,
                    pnrsIncluded,
                    extraHeader: this.getExtraHeader(disableExtraHeader, pnrsIncluded),
                    semantic,
                };
                const trackingInfo = this.getCancellationCTATrackingInfo(notificationData);
                this.setNotificationCTAHandler(trackingInfo, notificationData);
                notifications.push(notificationData);
            }
            return notifications;
        }, []);
    },

    getExtraHeader(disableExtraHeader, pnrsIncluded) {
        return !disableExtraHeader && Object.values(pnrsIncluded).some(({extraHeader}) => extraHeader);
    },

    getNotificationDisplayedForCancellationGroup(semantic, pnrsIncluded, cancellations) {
        if (cancellations.length > 1) {
            const allNotificationType = new Set(cancellations).size > 1 ? semantic : cancellations[0];
            return `ALL_${allNotificationType}`;
        } else if (cancellations.length > 0) {
            return Odigeo._.map(pnrsIncluded, 'cancellationStatus')[0];
        }
    },

    recalculateNotifications() {
        const currentNotifications = this.model.get('notificationsDisplayed');
        const resultingNotifications = this.model.get('isBIM') ? this.getBIMNotifications() : this.getNotificationsToDisplay();
        const diff = currentNotifications.filter((cn) => !Object.values(resultingNotifications).map((rn) => rn.status).includes(cn));
        diff.map(this.deleteNotification.bind(this));
        this.renderCancellationsNotifications(resultingNotifications);
    },

    renderCancellationsNotifications(notificationsToDisplay) {
        const intermediateLayerNotifications = this.getIntermediateLayerNotifications(notificationsToDisplay);
        const widgetReadyPromise = Odigeo._.result(this.openTicket, 'onViewReady', Promise.resolve());
        widgetReadyPromise.then(() => {
            notificationsToDisplay
                .filter((notification) => (notification?.status !== 'UNKNOWN'))
                .map(this.addNotification.bind(this));
        });
        if (intermediateLayerNotifications.length !== 0) {
            this.renderIntermediateLayerCancellations(intermediateLayerNotifications);
        }
        this.model.set('notificationsDisplayed', notificationsToDisplay.map(({status}) => status));
    },

    getIntermediateLayerNotifications(notificationsToDisplay) {
        return notificationsToDisplay
            .filter((n) => n.status !== BIM_STATUS.ALL_RESOLVED && Object.keys(n.pnrsIncluded).length > 1);
    },

    renderIntermediateLayerCancellations(intermediateLayerNotifications) {
        const cancellationsData = intermediateLayerNotifications.reduce((allCancellationsData, notificationData) => {
            const {semantic, pnrsIncluded, status} = notificationData;
            const cancellationsData = Odigeo._.map(pnrsIncluded, (pnrInfo, pnr) => {
                const trackingInfo = this.getCancellationCTATrackingInfo({
                    semantic,
                    status,
                    pnrsIncluded: Odigeo._.pick(pnrsIncluded, pnr),
                });
                const ctaHandler = this.getIntermediateLayerCancellationCardCTAHandler(trackingInfo, pnr, pnrInfo);
                return intermediateLayerHelper.getCancellationsData(this.keys, pnrInfo, ctaHandler);
            });
            return allCancellationsData.concat(cancellationsData);
        }, []);
        this.parentWidget.components.intermediateLayer.execute('renderCancellations', cancellationsData);
    },

    addNotification(notificationToDisplay) {
        const bookingInfo = this.model.get('bookingInfo');
        const notification = notificationHelper.getNotificationData(this.keys, notificationToDisplay, bookingInfo);
        this.parentWidget.components.notifications.execute('updateNotification', notification);
    },

    deleteAllNotifications() {
        if (this.parentWidget.components.notifications) {
            this.parentWidget.components.notifications.execute('deleteAllNotifications');
        }
    },

    deleteNotification(notificationId) {
        this.parentWidget.components.notifications.execute('deleteNotification', notificationId);
    },

    getPNRsByCancellation(...cancellationStatus) {
        return Odigeo._.pick(this.model.get('cancelledPNRs'), (pnrInfo) => cancellationStatus.includes(pnrInfo.cancellationStatus));
    },

    // AB SCOPES
    isNotificationsManagerV1Scope() {
        const cancelledPNRs = this.model.get('cancelledPNRs');
        return Object.keys(cancelledPNRs).length === 1;
    },

    isNotificationsManagerResolvedScope() {
        const cancelledPNRs = this.model.get('cancelledPNRs');
        const resolvedPNRs = this.getPNRsByCancellation(BIM_STATUS.RESOLVED);
        return (Object.keys(cancelledPNRs).length === Object.keys(resolvedPNRs).length);
    },

    isIntermediateLayerManagerScope() {
        return this.model.get('isIntermediateLayerManager');
    },

    isRenderPNRCards() {
        return !(this.isNotificationsManagerV1Scope() || this.isIntermediateLayerManagerScope() || this.isNotificationsManagerResolvedScope());
    },
    // CTAs
    setNotificationCTAHandler(trackingInfo, notificationData) {
        function unboundCTA(trackingInfo, {semantic, pnrsIncluded, status, cancellationType}) {
            this.trackCancellationCTAClick(trackingInfo, TRACKING.INCIDENTS_MANAGER_ACTION);
            if (cancellationType !== BIM_CANCELLATION_TYPE.VOLUNTARY && Object.keys(pnrsIncluded).length > 1 && status !== BIM_STATUS.ALL_RESOLVED) {
                this.parentWidget.components.intermediateLayer.execute('displayCancellationsWithType', semantic.toLowerCase(), trackingInfo)
                    .then(() => this.parentWidget.toggleIntermediateLayerVisibility(true));
            } else {
                this.handleCancellation(...Object.entries(pnrsIncluded)[0], cancellationType);
            }
        }

        notificationData.ctaHandler = unboundCTA.bind(this, trackingInfo, notificationData);
    },

    getIntermediateLayerCancellationCardCTAHandler(trackingInfo, pnr, pnrInfo) {
        function unboundCTA(trackingInfo, pnr, pnrInfo) {
            this.trackCancellationCTAClick(trackingInfo, TRACKING.INTERMEDIATE_LAYER_ACTION);
            if (Odigeo.Utils.isDesktop()) {
                this.parentWidget.toggleIntermediateLayerVisibility(false);
            }
            this.handleCancellation(pnr, pnrInfo);
        }

        return unboundCTA.bind(this, trackingInfo, pnr, pnrInfo);
    },

    handleCancellation(pnr, pnrInfo, cancellationType) {
        switch (pnrInfo.cancellationStatus) {
            case BIM_CANCELLATION_STATUS.OPEN_TICKET_ACCEPTED:
                return this.openTicket.execute('manageAcceptedFlexibleTicket', pnr);
            case BIM_CANCELLATION_STATUS.OPEN_TICKET_PENDING:
                return this.openTicket.execute('managePendingFlexibleTicket', pnr);
            case BIM_CANCELLATION_STATUS.OPEN_TICKET_REJECTED:
                return this.openTicket.execute('openRefundStatus', pnr, this.isNotificationsManagerV1Scope());
            case BIM_CANCELLATION_STATUS.REDEMPTION_PENDING:
                return this.openTicket.execute('openRedemptionStatus', pnr);
            case BIM_CANCELLATION_STATUS.MMB:
                return this.involuntaryCancellation.execute('mmbOpen');
            case BIM_CANCELLATION_STATUS.CONTACT_AIRLINE:
                return this.involuntaryCancellation.execute('contactAirlineOpen', pnr);
            case BIM_CANCELLATION_STATUS.REFUND_WAITING_CONSENT:
                return this.involuntaryCancellation.execute('openRefundAlternatives', pnr);
            case BIM_CANCELLATION_STATUS.REFUND:
            case BIM_CANCELLATION_STATUS.REFUND_MERCHANT:
            case BIM_CANCELLATION_STATUS.REFUND_NO_MERCHANT:
            case BIM_CANCELLATION_STATUS.REFUND_WITH_STATUS:
                return cancellationType === BIM_CANCELLATION_TYPE.VOLUNTARY ?
                    Odigeo.trigger('trip_details:toggleDownloadAppNotification') :
                    this.cashRefund.execute('openRefundStatus', pnr, this.isNotificationsManagerV1Scope());
            case BIM_CANCELLATION_STATUS.RESOLVED:
                return this.resolvedNotificationCTA();
            case BIM_CANCELLATION_STATUS.REFUND_NOT_MANAGED_BY_EDO:
                return this.refundNotManagedByEdoCTA();
            default:
                return;
        }
    },

    refundNotManagedByEdoCTA() {
        const bookingInfo = this.model.get('bookingInfo');
        Odigeo.Router.navigate(`manage-my-booking/manage-refund/${bookingInfo?.tdToken}`);
    },
    resolvedNotificationCTA() {
        const surveyUrl = this.buildResolvedSurveyLink();
        window.open(surveyUrl, '_blank');
    },

    buildResolvedSurveyLink() {
        const params = this.getSurveyParams();
        const queryString = Odigeo.Utils.encodeParams(params);
        return `${this.keys.surveyUrl}${queryString}`;
    },

    getSurveyParams() {
        const {brand, locale, website, flow} = Odigeo.SessionScope;
        const resolvedPNRs = this.getPNRsByCancellation(BIM_CANCELLATION_STATUS.RESOLVED);
        const airlinesSet = new Set(Object.values(resolvedPNRs).map((pnrInfo) => pnrInfo.validatingCarrier.code));
        return {
            BID: this.model.get('bookingID'),
            brand: SURVEY_BRAND_PARAM_MAP[brand],
            market: website.slice(-2),
            interface: flow,
            lang: SURVEY_LANG_EXCEPTIONS[locale] || locale.slice(0, 2),
            airline: Array.from(airlinesSet).join(),
            multipleResolvedPNRs: Object.keys(resolvedPNRs).length > 1,
        };
    },

    // RECLASSIFICATION
    acceptOpenTicket(pnr) {
        this.reclassificationHelper(pnr, BIM_UPDATE_CONSENT.OPEN_TICKET_ACCEPTED, BIM_CANCELLATION_STATUS.OPEN_TICKET_ACCEPTED, true);
    },

    rejectOpenTicket(pnr) {
        this.reclassificationHelper(pnr, BIM_UPDATE_CONSENT.OPEN_TICKET_REJECTED, BIM_CANCELLATION_STATUS.OPEN_TICKET_REJECTED);
    },

    airlineAlternativeAccepted(pnr) {
        this.reclassificationHelper(pnr, BIM_UPDATE_CONSENT.AIRLINE_ALTERNATIVE_ACCEPTED, BIM_CANCELLATION_STATUS.RESOLVED_BY_CUSTOMER);
    },

    cashRefundRequested(pnr) {
        this.reclassificationHelper(pnr, BIM_UPDATE_CONSENT.CASH_REFUND_REQUESTED, BIM_CANCELLATION_STATUS.REFUND);
    },

    reclassificationHelper(selectedPNR, updateConsentBIM, newCancellationStatus, avoidReload) {
        if (this.model.get('isBIM')) {
            const [firstIncident] = this.model.get('messages')
                .map(({cancellations}) => cancellations)
                .flat()
                .filter(({pnr}) => pnr === selectedPNR)
                .map(({incidents}) => incidents)
                .flat();
            this.model.updateConsent(firstIncident, updateConsentBIM)
                .then(() => this.persistBIM(avoidReload));
        } else {
            this.model.persistReclassification(selectedPNR, newCancellationStatus, updateConsentBIM);
            if (newCancellationStatus === BIM_CANCELLATION_STATUS.REFUND) {
                if (Odigeo._.isUndefined(this.cashRefund) || Odigeo._.isNull(this.cashRefund)) {
                    this.createCashRefundIfNeeded();
                } else {
                    this.cashRefund.execute('forceRender'); // Needed as in cashRefund there is no automatic re-rendering as in OT.
                }
            }
            if (!this.model.get('renderPNRCards')) {
                this.recalculateNotifications();
                this.parentWidget.toggleIntermediateLayerVisibility(false);
            }
        }
    },

    persistBIM(avoidReload) {
        if (!avoidReload) {
            this.reset();
        }
        this.model.set('avoidReload', avoidReload);
        this.model.reclassificationBIM();
    },

    // TRACKING
    trackWidgetOnloadLegacy() {
        const bookingInfo = this.model.get('bookingInfo');
        const cancelledPNRs = this.model.get('cancelledPNRs');
        const confirmedPNRsLength = bookingInfo.totalPnrsInBooking - Object.keys(cancelledPNRs).length;
        const confirmedStats = confirmedPNRsLength > 0 ? {ok: confirmedPNRsLength} : {};
        const pnrStats = this.getStats(cancelledPNRs, true, confirmedStats, '-');
        const trackingInfo = Object.assign({}, this.model.get('trackingInfo'), {PNR: pnrStats});
        const label = trackingUtils.composeLabel(TRACKING.ONLOAD_LABEL_PREFIX, TRACKING.LEGACY_ONLOAD_PARAMS, trackingInfo);
        trackingUtils.trackEvent(TRACKING.INCIDENTS_MANAGER_ACTION, label);
    },

    trackWidgetOnload(notificationsToDisplay) {
        const cancelledPNRs = this.model.get('cancelledPNRs');
        const allPNRStats = this.getStats(cancelledPNRs);
        const trackingInfo = Object.assign({}, this.model.get('trackingInfo'), {
            type: 'cancellation',
            status: notificationsToDisplay.map(({status}) => status.replace(/(_)/g, '-').toLowerCase()).join('&'),
            semantic: notificationsToDisplay.map(({semantic}) => semantic.replace(/(_)/g, '-').toLowerCase()).join('&'),
            includes: allPNRStats,
        });
        const label = trackingUtils.composeLabel(TRACKING.ONLOAD_LABEL_PREFIX, TRACKING.ONLOAD_PARAMS, trackingInfo);
        trackingUtils.trackEvent(TRACKING.INCIDENTS_MANAGER_ACTION, label);
    },

    trackInvalidCancellationProviderItem({id}) {
        const trackingInfo = Object.assign({}, this.model.get('trackingInfo'), {incident: id});
        const label = trackingUtils.composeLabel(TRACKING.INVALID_CANCELLATION_PREFIX, TRACKING.INVALID_CANCELLATION_PARAMS, trackingInfo);
        trackingUtils.trackEvent(TRACKING.INCIDENTS_MANAGER_ACTION, label);
    },

    trackCancellationCTAClick(trackingInfo, trackingAction) {
        const label = trackingUtils.composeLabel(TRACKING.CLICK_CTA_PREFIX, TRACKING.CLICK_CTA_PARAMS, trackingInfo);
        trackingUtils.trackEvent(trackingAction, label);
    },

    getCancellationCTATrackingInfo({semantic, pnrsIncluded, status}) {
        const includes = this.getStats(pnrsIncluded);
        const PNR = Object.keys(pnrsIncluded).join('&');
        return Object.assign({}, this.model.get('trackingInfo'), {
            type: 'cancellation',
            status: status.replace(/(_)/g, '-').toLowerCase(),
            includes,
            PNR,
            semantic: semantic.toLowerCase(),
        });
    },

    getStats(pnrs, useLegacyStatKeys = false, acc = {}, joinSeparator = '&') {
        const stats = Object.values(pnrs)
            .map(useLegacyStatKeys ? this.getStatKeyLegacy.bind(this) : this.getStatKey.bind(this))
            .reduce((stats, statKey) => {
                stats[statKey] = (stats[statKey] || 0) + 1;
                return stats;
            }, acc);
        return Object.keys(stats).map((key) => `${key}:${stats[key]}`).join(joinSeparator);
    },

    getStatKey({cancellationStatus, validatingCarrier: {code}, refundStatus, refund, merchant}) {
        let statKey = cancellationStatus;
        if (cancellationStatus === BIM_CANCELLATION_STATUS.CONTACT_AIRLINE) {
            statKey = `${cancellationStatus}-${code}`;
        } else if ([BIM_CANCELLATION_STATUS.REFUND,
            BIM_CANCELLATION_STATUS.OPEN_TICKET_REJECTED,
            BIM_CANCELLATION_STATUS.REFUND_WITH_STATUS].includes(cancellationStatus)) {
            statKey = refundStatus || (refund && refund.status) || `refund-${merchant ? '' : 'no-'}merchant`;
        }
        return statKey.replace(/(_)/g, '-').toLowerCase();
    },

    getStatKeyLegacy(pnrInfo) {
        const statKey = TRACKING.STATUS[pnrInfo.cancellationStatus];
        if (pnrInfo.cancellationStatus === BIM_CANCELLATION_STATUS.CONTACT_AIRLINE) {
            return `${statKey}-${pnrInfo.validatingCarrier.code}`;
        } else if (pnrInfo.cancellationStatus === BIM_CANCELLATION_STATUS.REFUND && pnrInfo.manualRefund) {
            return `manual-${statKey}-${pnrInfo.merchant ? '' : 'no-'}merchant`;
        }
        return statKey;
    },

    // MISC
    setSectionNames(bookingInfo) {
        const tripType = bookingInfo.itineraryBookings.bookingItinerary.tripType;
        const segments = bookingInfo.itineraryBookings.itinerarySegments;
        Odigeo._.map(this.model.get('openTicketPNRs'), (pnrInfo) => pnrInfo.sections).flat()
            .forEach((section) => section.sectionName = incidentsManagerUtils.getSectionName(section, tripType, segments));
    },

    navigateToMMB() {
        return this.parentWidget.navigateToNewMMB();
    },

    getIsPrimeUser() {
        return this.parentWidget.getIsPrimeUser();
    },

    getPrimeExpirationDate() {
        return this.parentWidget.getPrimeExpirationDate();
    },

    // TODO: Is not necessary in isBookingImportantMessage
    updateItineraryScheduleChanges(scheduleChangesPNRs) {
        this.parentWidget.updateItineraryScheduleChanges(scheduleChangesPNRs, this.model.get('cancelledPNRs'));
    },
})));
